import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Box,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  BACKENDS,
  getSettings,
  updateSettings,
  subscribeSettings,
  getPerfLog,
  subscribePerf,
  clearPerfLog,
  formatMs,
} from '../services/settings';

const backendChip = (id) => (
  <Chip
    size="small"
    label={id === 'rust' ? 'Rust' : 'C++'}
    color={id === 'rust' ? 'primary' : 'default'}
    sx={{ height: 20, fontSize: '0.7rem' }}
  />
);

/**
 * Summarise the log: for each (query, dictionary, offset) that was run on
 * both backends, show the median round-trip of each so they can be compared.
 */
function compareRows(log) {
  const groups = new Map();
  for (const e of log) {
    if (e.error) continue;
    const key = `${e.dictionary} ${e.query} ${e.offset}`;
    if (!groups.has(key)) groups.set(key, { query: e.query, dictionary: e.dictionary, offset: e.offset, rust: [], legacy: [] });
    groups.get(key)[e.backend]?.push(e.roundTripMs);
  }
  const median = (xs) => {
    if (!xs.length) return null;
    const s = [...xs].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
  };
  return [...groups.values()]
    .map((g) => ({ ...g, rust: median(g.rust), legacy: median(g.legacy), n: g.rust.length + g.legacy.length }))
    .filter((g) => g.rust !== null && g.legacy !== null);
}

function SettingsDialog({ open, onClose }) {
  const [settings, setSettings] = useState(getSettings());
  const [log, setLog] = useState(getPerfLog());

  useEffect(() => subscribeSettings(setSettings), []);
  useEffect(() => subscribePerf(setLog), []);

  const comparisons = compareRows(log);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Settings</DialogTitle>
      <DialogContent dividers>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Search backend</FormLabel>
          <RadioGroup
            value={settings.backend}
            onChange={(e) => updateSettings({ backend: e.target.value })}
          >
            {BACKENDS.map((b) => (
              <FormControlLabel
                key={b.id}
                value={b.id}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">{b.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{b.description}</Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5 }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.showTiming}
              onChange={(e) => updateSettings({ showTiming: e.target.checked })}
            />
          }
          label={<Typography variant="body2">Show timing with results</Typography>}
          sx={{ mb: 2, display: 'flex' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2">Recent timings</Typography>
          <Button size="small" onClick={clearPerfLog} disabled={log.length === 0}>Clear</Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Round trip is measured in the browser. Server time and steps are reported by the
          Rust backend; the legacy backend does not report them. Run the same query on both
          backends to fill the comparison table.
        </Typography>

        {comparisons.length > 0 && (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Query</TableCell>
                <TableCell>Dictionary</TableCell>
                <TableCell align="right">Rust</TableCell>
                <TableCell align="right">C++</TableCell>
                <TableCell align="right">Speedup</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisons.map((c) => (
                <TableRow key={`${c.dictionary}-${c.query}-${c.offset}`}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {c.query}{c.offset ? <Typography component="span" variant="caption" color="text.secondary"> (page {c.offset / 50 + 1})</Typography> : null}
                  </TableCell>
                  <TableCell>{c.dictionary}</TableCell>
                  <TableCell align="right">{formatMs(c.rust)}</TableCell>
                  <TableCell align="right">{formatMs(c.legacy)}</TableCell>
                  <TableCell align="right">
                    {c.rust > 0 ? `${(c.legacy / c.rust).toFixed(1)}x` : '–'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {log.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No searches yet.</Typography>
        ) : (
          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Backend</TableCell>
                  <TableCell>Query</TableCell>
                  <TableCell>Dictionary</TableCell>
                  <TableCell align="right">Round trip</TableCell>
                  <TableCell align="right">Server</TableCell>
                  <TableCell align="right">Steps</TableCell>
                  <TableCell align="right">Results</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {log.map((e) => (
                  <TableRow key={e.id} sx={{ opacity: e.error ? 0.6 : 1 }}>
                    <TableCell>{backendChip(e.backend)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={e.query}><span>{e.query}</span></Tooltip>
                      {e.offset ? <Typography component="span" variant="caption" color="text.secondary"> (page {e.offset / 50 + 1})</Typography> : null}
                    </TableCell>
                    <TableCell>{e.dictionary}</TableCell>
                    <TableCell align="right">{formatMs(e.roundTripMs)}</TableCell>
                    <TableCell align="right">
                      {e.serverMs === null || e.serverMs === undefined ? '–' : (
                        <Tooltip title={e.cached ? 'served from the cached session' : 'search work for this request'}>
                          <span>{e.cached ? 'cached' : formatMs(e.serverMs)}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">{e.steps ? e.steps.toLocaleString() : '–'}</TableCell>
                    <TableCell align="right">
                      {e.error ? <Chip size="small" color="error" label="error" sx={{ height: 20, fontSize: '0.7rem' }} /> : e.results}
                      {e.limitReached && !e.error ? <Typography component="span" variant="caption" color="text.secondary"> (limit)</Typography> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SettingsDialog;
